import {
  BoxProps,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputProps,
  NumberInputStepper,
} from '@chakra-ui/react';
import useTranslate from '~/common/utils/translate';
import { FormLabelProps } from '@material-ui/core';
import { FieldInputProps, useField } from 'formik';
import React from 'react';

type DefaultNumberInputProps = NumberInputProps & {
  name: string;
  label: string;
  step?: number;
  min?: number;
  max?: number;
  allowDecimalsButMaxTwo?: boolean;
  row?: boolean;
  onChange?: (value: any) => void;
  params?: string[];
  errorParams?: string[];
  dataType?: 'int' | 'float';
  maxInputLenght?: number;
  labelProps?: FormLabelProps & BoxProps;
  /**
   * @deprecated prop has no effect anymore
   */
  autoFocus?: boolean;
  /**
   * @deprecated prop has no effect anymore
   */
  type?: string;
  /**
   * @deprecated use isDisabled instead
   */
  disabled?: boolean;
};

export const DefaultNumberInputField: React.FC<DefaultNumberInputProps> = ({
  label,
  size: _,
  step,
  name,
  row = false,
  allowDecimalsButMaxTwo = false,
  onChange,
  params,
  errorParams,
  dataType = 'int',
  maxInputLenght: maxInputLength,
  labelProps,
  disabled,
  isDisabled,
  ...props
}) => {
  const [field, { error }] = useField({
    ...(props as FieldInputProps<any>),
    name,
  });
  const translate = useTranslate();

  const lastCharIsADot = (value: string) => {
    return value.substring(value.length - 1) === '.';
  };

  const convertToNumber = (value: string) => {
    let n: number;
    const amountOfDotsIsExceeded = allowDecimalsButMaxTwo ? 3 : 2;
    if (value.split('.').length === amountOfDotsIsExceeded)
      value = value.substring(0, value.length - 1);
    if (allowDecimalsButMaxTwo) {
      const splitedValues = value.split('.');
      if (splitedValues.length === 2) {
        const decimals = parseInt(splitedValues[1], 10);
        if (decimals > 99) value = value.substring(0, value.length - 1);
      }
    }
    if (!lastCharIsADot(value)) {
      if (dataType === 'int' && !allowDecimalsButMaxTwo) {
        n = parseInt(value, 10);
      } else {
        n = parseFloat(value);
      }
      return Number.isNaN(n) ? 0 : n;
    }
    return value;
  };

  const handleChange = (inputValue: string) => {
    let value = inputValue;
    if (maxInputLength && value.length > maxInputLength) {
      value = field.value as string;
    }
    if (onChange) onChange(convertToNumber(value));
    field.onChange({
      type: 'number',
      target: {
        value: convertToNumber(value),
        name,
      },
    });
  };

  return (
    <FormControl isInvalid={!!error}>
      <Flex direction={row ? 'row' : 'column'} align="start">
        {label && (
          <FormLabel htmlFor={field.name} {...labelProps}>
            {translate(label, params)}
          </FormLabel>
        )}
        <NumberInput
          {...field}
          {...props}
          isDisabled={disabled ?? isDisabled}
          defaultValue={field.value as string}
          step={step}
          onChange={handleChange}
          borderColor="#807a7a"
        >
          <NumberInputField name={name} />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
      </Flex>
      {error ? (
        <FormErrorMessage>{translate(error, errorParams)}</FormErrorMessage>
      ) : null}
    </FormControl>
  );
};
